/* eslint-disable prefer-destructuring */
import {
	BoundingSphere,
	CallbackProperty,
	Cartesian2,
	Cartesian3,
	Cartographic,
	ClassificationType,
	Color,
	DataSourceDisplay,
	DataSourceCollection,
	Entity,
	Ellipsoid,
	HeadingPitchRoll,
	Math as CesiumMath,
	Matrix4,
	Model,
	Resource,
	sampleTerrainMostDetailed,
	Transforms,
} from 'cesium';
import gltfBoundingBox from 'gltf-bounding-box';
import { getGeom } from '@turf/invariant';
import { point } from '@turf/helpers';

export async function adjustModelByHeadingPitchRollMatrix(
	id,
	lon,
	lat,
	height,
	heading = 0,
	pitch = 0,
	roll = 0,
	scale = 1,
) {
	const cesiumViewer = this.getCesiumData().viewer;
	const primitive = this.getPrimitiveById(id);

	if (lon) {
		primitive.metadata.position.longitude = lon;
	} else {
		lon = primitive.metadata.position.longitude;
	}
	if (lat) {
		primitive.metadata.position.latitude = lat;
	} else {
		lat = primitive.metadata.position.longitude;
	}

	const terrainProvider = cesiumViewer.terrainProvider;
	const positions = [Cartographic.fromDegrees(lon, lat)];
	const pos = await sampleTerrainMostDetailed(terrainProvider, positions);
	height =
		!height && !primitive.metadata.height
			? pos[0].height
			: height || primitive.metadata.height;
	const center = Cartesian3.fromDegrees(lon, lat, height);

	if (!height) {
		height = pos[0].height;
	}
	const hpv = new HeadingPitchRoll(
		(heading * CesiumMath.PI) / 180,
		(-pitch * CesiumMath.PI) / 180,
		(roll * CesiumMath.PI) / 180
	);

	const modelMatrix = Transforms.headingPitchRollToFixedFrame(center, hpv);
	if (!scale && !primitive.metadata.scale) {
		scale = 1;
	} else if (!scale && primitive.metadata.scale) {
		scale = primitive.metadata.scale;
	}
	await Matrix4.multiplyByUniformScale(modelMatrix, scale, modelMatrix);

	primitive.modelMatrix = modelMatrix;
	const zoomSphere = new BoundingSphere(
		center,
		primitive.boundingSphere.radius * 2
	);
	const dimensions = Object.fromEntries(Object.entries(primitive.metadata.dimensions).map(entry => [entry[0], entry[1] * scale]));
	const modelData = {
		position: { longitude: lon, latitude: lat },
		height,
		rotation: heading || 0,
		pitch: pitch || 0,
		roll: roll || 0,
		scale,
		center,
		dimensions,
		zoomSphere,
	};

	primitive.metadata = modelData;

	cesiumViewer.scene.requestRender();
	if (!this.dragMode)
		this.updateEntityData(id, {
			data: modelData,
		});
}

export function setFocusedModel(id) {
	// this.shapeAuthoringWorkflow.lineEntity.show = true;
	const selectedEntity = this.$_linearEntities[id];
	this.modelWorkflow.focusedName = selectedEntity.name;
	this.modelWorkflow.focusedId = id;
	this.modelWorkflow = { ...this.modelWorkflow, ...selectedEntity.data[0].data };
	console.log(this.modelWorkflow.rotation);
	this.mouseWheel = Number(this.modelWorkflow.rotation);

}

export function getMapCenter() {
	const cesiumViewer = this.getCesiumData().viewer;
	const windowPosition = new Cartesian2(
		cesiumViewer.container.clientWidth / 2,
		cesiumViewer.container.clientHeight / 2
	);
	const pickRay = cesiumViewer.scene.camera.getPickRay(windowPosition);
	const pickPosition = cesiumViewer.scene.globe.pick(
		pickRay,
		cesiumViewer.scene
	);
	const pickPositionCartographic = cesiumViewer.scene.globe.ellipsoid.cartesianToCartographic(
		pickPosition
	);

	const longitude = pickPositionCartographic.longitude * (180 / Math.PI);
	const latitude = pickPositionCartographic.latitude * (180 / Math.PI);
/* 	const terrainProvider = cesiumViewer.terrainProvider;
	const positions = [Cartographic.fromDegrees(longitude, latitude)];
	const pos = await sampleTerrainMostDetailed(terrainProvider, positions);

	const height = pos[0].height; */
	return { longitude, latitude };
}
export function scaleModel(id = this.modelWorkflow.focusedId, scale = +this.modelWorkflow.scale) {
	const primitive = this.getPrimitiveById(id);
	const longitude = primitive.metadata.position.longitude;
	const latitude = primitive.metadata.position.latitude;
	primitive.metadata.scale = scale;

	this.adjustModelByHeadingPitchRollMatrix(
		primitive.id,
		longitude,
		latitude,
		null,
		primitive.metadata.rotation,
		null,
		null,
		scale
	);
}
export function rotateModel(
	id = this.modelWorkflow.focusedId,
	rotation = +this.modelWorkflow.rotation
) {
	const primitive = this.getPrimitiveById(id);

	const longitude = primitive.metadata.position.longitude;
	const latitude = primitive.metadata.position.latitude;
	primitive.metadata.rotation = rotation;
	this.adjustModelByHeadingPitchRollMatrix(
		primitive.id,
		longitude,
		latitude,
		null,
		rotation,
		null,
		null,
		primitive.metadata.scale
	);
}
export async function moveModel(id = this.modelWorkflow.focusedId, position) {
	const positionCartographic = Ellipsoid.WGS84.cartesianToCartographic(
		position
	);
	const height = await this.getTerrainHeight(position);
	const latitude = CesiumMath.toDegrees(positionCartographic.latitude);
	const longitude = CesiumMath.toDegrees(positionCartographic.longitude);
	const primitive = this.getPrimitiveById(id);
	this.adjustModelByHeadingPitchRollMatrix(
		primitive.id,
		longitude,
		latitude,
		height,
		primitive.metadata.rotation || null,
		null,
		null,
		primitive.metadata.scale || null
	);
	this.modelWorkflow.moveActive = false;
}
export function heightOfModel(
	id = this.modelWorkflow.focusedId,
	height = +this.modelWorkflow.height
) {
	const primitive = this.getPrimitiveById(id);

	const longitude = primitive.metadata.position.longitude;
	const latitude = primitive.metadata.position.latitude;

	primitive.metadata.height = height;
	this.adjustModelByHeadingPitchRollMatrix(
		primitive.id,
		longitude,
		latitude,
		height,
		primitive.metadata.rotation,
		null,
		null,
		primitive.metadata.scale
	);
}
export function toggleDebugWireframeModel(id = this.modelWorkflow.focusedId) {
	const cesiumViewer = this.getCesiumData().viewer;
	const primitive = this.getPrimitiveById(id);
	console.log(primitive);
	primitive.debugWireframe = !primitive.debugWireframe;
	cesiumViewer.scene.requestRender();
}

export function createOrientation(degrees) {
	const cesiumViewer = this.getCesiumData().viewer;
	const hpr = new HeadingPitchRoll((degrees * CesiumMath.PI) / 180, 0, 0);
	const { x, y, z } = this.modelWorkflow.center;
	const origin = new Cartesian3(x, y, z);
	const ellipsoid = cesiumViewer.scene.globe.ellipsoid;
	const quaternion = Transforms.headingPitchRollQuaternion(origin, hpr, ellipsoid);
	return quaternion;
}

export async function loadGltf(assetId, metadata) {
	const cesiumViewer = this.getCesiumData().viewer;
	const terrainProvider = cesiumViewer.terrainProvider;
	const positions = [
		Cartographic.fromDegrees(
			metadata.position.longitude,
			metadata.position.latitude
		),
	];
	await sampleTerrainMostDetailed(terrainProvider, positions);
	metadata.height = !metadata.height ? positions[0].height : metadata.height;

	const center = Cartesian3.fromDegrees(
		metadata.position.longitude,
		metadata.position.latitude,
		metadata.height
	);

	const hpv = new HeadingPitchRoll(
		(metadata.rotation * CesiumMath.PI) / 180,
		(-metadata.pitch * CesiumMath.PI) / 180,
		(metadata.roll * CesiumMath.PI) / 180
	);

	const modelMatrix = Transforms.headingPitchRollToFixedFrame(center, hpv);

	await Matrix4.multiplyByUniformScale(
		modelMatrix,
		metadata.scale,
		modelMatrix
	);
	this.modelWorkflow.focusedId = assetId;
	this.modelWorkflow.height = metadata.height;
	this.modelWorkflow.rotation = metadata.rotation || 0;
	this.modelWorkflow.scale = metadata.scale;
	const url = `${this.axios.defaults.baseURL}files/${metadata.uri}`;
	const headers = this.axios.defaults.headers.common;

	const loadUrl = new Resource({
		url,
		headers,
		retryAttempts: 10
	});
	const resourceGltf = cesiumViewer.scene.primitives.add(
		Model.fromGltf({
			id: assetId,
			scene: cesiumViewer.scene,
			url: loadUrl,
			show: true,
			modelMatrix,
			scale: metadata.scale,
		})
	);

	await resourceGltf.readyPromise
		.then(() => {
			resourceGltf.metadata = metadata;
			const zoomSphere = new BoundingSphere(
				center,
				resourceGltf.boundingSphere.radius * 2
			);
			resourceGltf.metadata.zoomSphere = zoomSphere;
		});
		
}

export async function importModelFile(file) {
	if (!file.id) throw new Error();
	const name = file.name;
	const gltf = await this.axios.get(`files/${file.id}`);
	const { dimensions } = gltfBoundingBox.computeBoundings(gltf.data);
	const cesiumViewer = this.getCesiumData().viewer;
	let lon;
	let lat;
		if (this.modelWorkflow.position.height !== 0) {
			lon = this.modelWorkflow.position.longitude;
			lat = this.modelWorkflow.position.latitude;
		} else {
			const mapCenter = this.getMapCenter();			
			lon = mapCenter.longitude;
			lat = mapCenter.latitude;
		}
	
	const terrainProvider = cesiumViewer.terrainProvider;
	const positions = [Cartographic.fromDegrees(lon, lat)];
	await sampleTerrainMostDetailed(terrainProvider, positions);
	const height =  positions[0].height;
	const center = Cartesian3.fromDegrees(lon, lat, height);
	const zoomSphere = new BoundingSphere(center, height * 2.5);

	const metadata = {
		position: { longitude: lon, latitude: lat },
		height,
		rotation: 0,
		pitch: 0,
		roll: 0,
		scale: 1,
		center,
		zoomSphere,
	};
	const geometry = getGeom(
		point([metadata.position.longitude, metadata.position.latitude])
	);
	metadata.geometry = geometry;
	metadata.dimensions = dimensions;
	metadata.uri = file.id;
	metadata.name = name;
	metadata.type = 'model';
	return metadata;

}


